/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import ClientService from "../rest/clientService";
import CustomerService from "../rest/customerService";
import { FaCalendarAlt, FaCheckCircle, FaCoffee, FaUtensils, FaSun, FaMoon, FaCookieBite } from "react-icons/fa";
import { MdAccessTime, MdLockClock, MdBlock } from "react-icons/md";

// Brazil (America/Sao_Paulo) = UTC-3, no DST since April 2019
const getBrasiliaDateString = () => {
    const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getBrasiliaMinutes = () => {
    const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
    return d.getUTCHours() * 60 + d.getUTCMinutes();
};

const ReservationDatePicker = ({ onSelectionChange = () => { }, studentLogin = null }) => {
    const dateInputRef = useRef(null);

    const formatDateFriendly = (dateStr) => {
        if (!dateStr) return "Selecione uma data";
        const [y, m, d] = dateStr.split("-").map(Number);
        const date = new Date(y, m - 1, d, 12, 0, 0);
        return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const [turns, setTurns] = useState([]);
    const [preferredTurnIds, setPreferredTurnIds] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getBrasiliaDateString());
    const [selectedTurn, setSelectedTurn] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingPreferences, setLoadingPreferences] = useState(!!studentLogin);

    const handleInputClick = () => {
        try {
            if (dateInputRef.current && typeof dateInputRef.current.showPicker === "function") {
                dateInputRef.current.showPicker();
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        loadTurns();
    }, []);

    useEffect(() => {
        if (!studentLogin) {
            setPreferredTurnIds([]);
            setLoadingPreferences(false);
            return;
        }
        setLoadingPreferences(true);
        CustomerService.getStudentTurnPreferences(studentLogin)
            .then(ids => setPreferredTurnIds(ids || []))
            .catch(() => setPreferredTurnIds([]))
            .finally(() => setLoadingPreferences(false));
    }, [studentLogin]);

    const loadTurns = async () => {
        setLoading(true);
        try {
            const result = await ClientService.getTurns();
            const normalized = (result || []).map(t => ({
                turnId: t.turnId,
                name: t.name,
                start: (t.start ?? t.startTime ?? "").trim(),
                end: (t.end ?? t.endTime ?? "").trim(),
                isActive: t.isActive !== false
            }));
            setTurns(normalized);
        } catch (err) {
            console.error("Erro ao carregar turnos:", err);
        } finally {
            setLoading(false);
        }
    };

    const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const parts = timeStr.trim().split(":").map(Number);
        if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
        return { h: parts[0], m: parts[1] };
    };

    const isTurnDisabledOnDate = (turn, dateStr) => {
        if (!dateStr) return false;
        const todayStr = getBrasiliaDateString();

        // Past date: always disabled
        if (dateStr < todayStr) return true;
        // Future date: always enabled
        if (dateStr > todayStr) return false;

        // Today: disabled only if turn has already ended
        const endParts = parseTime(turn.end);
        if (!endParts) return false;

        const nowMinutes = getBrasiliaMinutes();
        const endMinutes = endParts.h === 0 && endParts.m === 0 ? 1440 : endParts.h * 60 + endParts.m;
        return nowMinutes > endMinutes;
    };

    useEffect(() => {
        if (!selectedDate || !selectedTurn) return;

        const todayStr = getBrasiliaDateString();
        let hour, minute;

        const startParts = parseTime(selectedTurn.start);
        const endParts = parseTime(selectedTurn.end);
        const turnStartHour = startParts?.h ?? 0;
        const turnStartMinute = startParts?.m ?? 0;
        const turnEndHour = endParts?.h ?? 0;
        const turnEndMinute = endParts?.m ?? 0;

        if (selectedDate === todayStr) {
            const nowMinutes = getBrasiliaMinutes();
            const startMinutes = turnStartHour * 60 + turnStartMinute;
            const endMinutesAdj = turnEndHour === 0 && turnEndMinute === 0 ? 1440 : turnEndHour * 60 + turnEndMinute;
            const isTurnInProgress = nowMinutes >= startMinutes && nowMinutes <= endMinutesAdj;

            if (isTurnInProgress) {
                const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
                hour = d.getUTCHours();
                minute = d.getUTCMinutes();
            } else {
                hour = turnStartHour;
                minute = turnStartMinute;
            }
        } else {
            hour = turnStartHour;
            minute = turnStartMinute;
        }

        const [y, mo, dy] = selectedDate.split("-");
        const finalDate =
            `${dy}/${mo}/${y} ` +
            `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

        onSelectionChange({
            turn: selectedTurn,
            finalDate: finalDate
        });
    }, [selectedDate, selectedTurn]);

    const handleTurnClick = (turn, disabled) => {
        if (disabled) return;
        setSelectedTurn(prev =>
            String(prev?.turnId) === String(turn.turnId) ? null : turn
        );
    };

    const getTurnVisuals = (name) => {
        const lower = name.toLowerCase();
        if (lower.includes('café') || lower.includes('manhã')) return { icon: FaCoffee, bg: "bg-orange-400" };
        if (lower.includes('almoço')) return { icon: FaUtensils, bg: "bg-red-500" };
        if (lower.includes('lanche') || lower.includes('tarde')) return { icon: FaCookieBite, bg: "bg-purple-500" };
        if (lower.includes('jantar') || lower.includes('noite')) return { icon: FaMoon, bg: "bg-indigo-900" };
        return { icon: FaSun, bg: "bg-blue-400" };
    };

    const displayedTurns = studentLogin
        ? turns.filter(t => preferredTurnIds.includes(t.turnId))
        : turns;

    const isLoadingAny = loading || loadingPreferences;

    return (
        <div className="flex-col justify-center w-full h-full">

            <div className="flex justify-center mt-4 mb-6 px-4">
                <div className="relative w-full md:w-2/3 lg:w-1/2 bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden group hover:border-custom_blue transition-colors">
                    <input
                        ref={dateInputRef}
                        type="date"
                        value={selectedDate}
                        onClick={handleInputClick}
                        onChange={(e) => {
                            setSelectedDate(e.target.value);
                            setSelectedTurn(null);
                        }}
                        className="
                            absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer
                            [&::-webkit-calendar-picker-indicator]:absolute
                            [&::-webkit-calendar-picker-indicator]:w-full
                            [&::-webkit-calendar-picker-indicator]:h-full
                            [&::-webkit-calendar-picker-indicator]:opacity-0
                        "
                        required
                    />

                    <div className="flex items-center p-4 relative z-10 pointer-events-none">
                        <div className="bg-blue-100 p-3 rounded-full text-custom_blue mr-4">
                            <FaCalendarAlt size={24} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Data da Reserva</span>
                            <span className="text-lg md:text-xl font-bold text-gray-800 capitalize">
                                {formatDateFriendly(selectedDate)}
                            </span>
                        </div>
                        <div className="ml-auto text-custom_blue font-semibold text-sm bg-blue-50 px-3 py-1 rounded-full border border-blue-100 group-hover:bg-custom_blue group-hover:text-white transition-colors">
                            Alterar
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-center my-4 w-full">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full px-2 md:px-0">

                    {isLoadingAny && (
                         <div className="col-span-full flex flex-col items-center justify-center py-10 opacity-50">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom_blue mb-2"></div>
                            <p className="text-gray-500 text-sm">Carregando horários...</p>
                        </div>
                    )}

                    {!isLoadingAny && displayedTurns.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center py-10 opacity-50">
                            <p className="text-gray-500 text-sm">Nenhum turno disponível para este aluno.</p>
                        </div>
                    )}

                    {!isLoadingAny && displayedTurns.map((turn) => {
                        const adminDisabled = !turn.isActive;
                        const timeDisabled = !adminDisabled && isTurnDisabledOnDate(turn, selectedDate);
                        const isBlocked = adminDisabled || timeDisabled;
                        const isSelected = !isBlocked && String(selectedTurn?.turnId) === String(turn.turnId);
                        const { icon: Icon, bg } = getTurnVisuals(turn.name);

                        return (
                            <div
                                key={turn.turnId}
                                onClick={() => handleTurnClick(turn, isBlocked)}
                                className={`
                                    group relative aspect-[4/5] rounded-2xl overflow-hidden transition-all duration-300
                                    ${isSelected ? "ring-4 ring-custom_blue shadow-xl scale-[1.02]" : "ring-1 ring-slate-200 shadow-md hover:shadow-lg hover:scale-[1.01]"}
                                    ${isBlocked ? "grayscale opacity-60 cursor-not-allowed bg-gray-100" : "cursor-pointer bg-white"}
                                `}
                            >
                                <div className={`absolute inset-0 ${bg} opacity-20 transition-opacity group-hover:opacity-30`} />

                                <div className={`absolute -right-4 -top-4 text-${bg.replace('bg-', '')} opacity-10 transform rotate-12`}>
                                     <Icon size={140} className={isBlocked ? "text-gray-500" : ""} />
                                </div>

                                <div className="absolute inset-0 flex items-center justify-center pb-8">
                                    {adminDisabled ? (
                                        <MdBlock className="text-gray-400 text-6xl drop-shadow-sm" />
                                    ) : timeDisabled ? (
                                        <MdLockClock className="text-gray-400 text-6xl drop-shadow-sm" />
                                    ) : (
                                        <Icon className={`${isSelected ? "text-custom_blue" : "text-gray-600"} text-6xl drop-shadow-sm transition-colors`} />
                                    )}
                                </div>

                                {isSelected && (
                                    <div className="absolute top-3 right-3 z-20 bg-white rounded-full p-1 shadow-sm animate-in fade-in zoom-in">
                                        <FaCheckCircle className="text-custom_blue text-xl" />
                                    </div>
                                )}

                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />

                                <div className="absolute bottom-0 left-0 w-full p-4 flex flex-col justify-end text-white z-10">
                                    <h3 className="font-bold text-xl leading-tight drop-shadow-md">
                                        {turn.name}
                                    </h3>

                                    <div className="flex items-center gap-2 mt-2 text-gray-200 text-sm font-medium border-t border-white/20 pt-2">
                                        <MdAccessTime />
                                        <span>{turn.start} - {turn.end}</span>
                                    </div>

                                    {adminDisabled && (
                                        <div className="mt-2 text-xs font-bold text-red-300 uppercase tracking-wide bg-red-900/40 w-fit px-2 py-1 rounded">
                                            Desabilitado pelo administrador
                                        </div>
                                    )}

                                    {timeDisabled && (
                                        <div className="mt-2 text-xs font-bold text-red-300 uppercase tracking-wide bg-red-900/40 w-fit px-2 py-1 rounded">
                                            Encerrado
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ReservationDatePicker;
